import React from 'react'
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { Box } from '@mui/material';
import AdharCard from './AdharCard';
import DOBCertificate from './DOBCertificate';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { makeStyles } from 'tss-react/mui';
import dayjs from 'dayjs';
import { Regex } from '../../../Utils/regex';
import IdProof from './IdProof';
import AddressProof from './AddressProof';
import AdditionalDocument from './AdditionalDocument';

const useStyles = makeStyles()((theme) => {
    return {
        TabList: {
            "& .MuiTabs-indicator": {
                display: "none",
            },
            "& .Mui-selected": {
                backgroundColor: "#3E4094",
                color: "#FFFFFF !important",
            },
            "& .MuiTabs-flexContainer": {
                display: "flex",
                justifyContent: "space-between",
            },
            "& .MuiTab-root": {
                textTransform: "capitalize",
                flex: 1,
                borderRight: "1px solid #3E4094",
                ":last-of-type": {
                    borderRight: "none",
                    borderRadius: "0 6px 6px 0"
                },
                ":first-of-type": {
                    borderRadius: "6px 0 0 6px",
                },
                "&.Mui-selected:first-of-type": {
                    borderRadius: "6px 0 0 6px",
                },
                display: "flex",
                alignItems: "center",
                gap: "8px",
            },
        }
    }
})

const UploadDocuments = ({
    documentTab,
    setDocumentTab,
    data,
    setData,
    sectionList,
    _addUpdateMember,
    handleUpload
}) => {
    // ---------- Hook -----------
    const { classes } = useStyles();
    const uploadTabs = sectionList?.map((val) => val?.uploadDetails)?.[0]?.fields

    // --------------- handleChange ---------------
    const handleChange = ({ target: { name, value }, section }) => {
        if (name === "panCardNumber") {
            value = value.toUpperCase();
        }
        setData(prevData => ({
            ...prevData,
            uploadDocuments: {
                ...prevData.uploadDocuments,
                [section]: {
                    ...(prevData.uploadDocuments[section] || {}),  // ensure that the section exists
                    [name]: value?.trimStart(),  // Update only the specified field
                }
            }
        }));
    };

    const handleChangeNumber = ({ target: { name, value }, section, num }) => {
        if (Regex.number.test(value) && value.length <= 12) {
            setData(prevData => ({
                ...prevData,
                uploadDocuments: {
                    ...prevData.uploadDocuments,
                    [section]: {
                        ...(prevData.uploadDocuments[section] || {}),  // ensure that the section exists
                        [name]: +value?.trimStart(),  // Update only the specified field
                    }
                }
            }));
        }
    };

    const handleFilterChange = (date, { target: { name }, section }) => {
        // Format the date before updating the specific field inside the section 
        setData(prevData => ({
            ...prevData,
            uploadDocuments: {
                ...prevData.uploadDocuments,
                [section]: {
                    ...prevData.uploadDocuments[section],
                    // [name]: dayjs(date).format('DD/MM/YYYY'), // Format and update the field
                    [name]: date ? dayjs(date).format('DD/MM/YYYY') : null,
                }
            }
        }));
    };

    const tabContent = [
        {
            lebal: "Aadhar Card",
            content: <AdharCard
                data={data}
                setDocumentTab={setDocumentTab}
                setData={setData}
                handleChange={(e) => handleChange({ ...e, section: "aadharCard" })}
                handleChangeNumber={handleChangeNumber}
                handleFilterChange={(date, e) => handleFilterChange(date, { ...e, section: "aadharCard" })}
                handleUpload={handleUpload}
            />
        },
        {
            lebal: "Id Proof",
            content: <IdProof
                data={data}
                setDocumentTab={setDocumentTab}
                setData={setData}
                handleChangeNumber={handleChangeNumber}
                handleChange={(e) => handleChange({ ...e, section: "IdProof" })}
                handleFilterChange={(date, e) => handleFilterChange(date, e, { section: "IdProof" })}
                handleUpload={handleUpload}
            />
        },
        {
            lebal: "DOB Certificate",
            content: <DOBCertificate
                data={data}
                setDocumentTab={setDocumentTab}
                setData={setData}
                handleChange={(e) => handleChange({ ...e, section: "lc_dob_certificate" })}
                handleFilterChange={(date, e) => handleFilterChange(date, e, { section: "lc_dob_certificate" })}
                handleUpload={handleUpload}
            />
        },
        {
            lebal: "Address Proof",
            content: <AddressProof
                data={data}
                setDocumentTab={setDocumentTab}
                setData={setData}
                handleFilterChange={(date, e) => handleFilterChange(date, e, { section: "addressProof" })}
                _addUpdateMember={_addUpdateMember}
                handleUpload={handleUpload}
            />
        },
        {
            lebal: "Additional Document",
            content: <AdditionalDocument
                data={data}
                setDocumentTab={setDocumentTab}
                setData={setData}
                handleFilterChange={(date, e) => handleFilterChange(date, e, { section: "additionalDocument" })}
                _addUpdateMember={_addUpdateMember}
                handleUpload={handleUpload}
            />
        }
    ]

    return (
        <Box sx={{ width: '100%', typography: 'body1' }}>
            <TabContext value={documentTab}>
                <Box sx={{
                    border: "1px solid #3E4094", borderRadius: "10px",
                    overflowX: "auto",
                    whiteSpace: "nowrap",
                    width: "100%",
                    padding: '0px, 10px'
                }}>
                    <TabList
                        onChange={(e, newValue) => setDocumentTab(newValue)}
                        aria-label="tabs with icons"
                        className={classes.TabList}
                        sx={{
                            display: "flex",
                            gap: 0, // No gaps between tabs
                            width: "max-content", // Allow content to exceed container width for scrolling
                            minWidth: "100%",
                        }}
                    >
                        {uploadTabs?.map((val, i) => (
                            <Tab
                                key={i}
                                label={val?.fieldName}
                                sx={{
                                    minHeight: "50px",
                                    width: { xs: "auto", md: `${100 / uploadTabs?.length}%` }, // Equal width on big screens
                                    textAlign: "center",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    display: "flex",
                                    whiteSpace: "nowrap", // Prevent text wrapping
                                }}
                                icon={<CheckCircleIcon sx={{ height: "20px", width: "20px" }} />}
                                iconPosition="end"
                                value={val?.indexNo}
                            />
                        ))}
                    </TabList>
                </Box>

                {tabContent?.map((val, i) => (
                    <TabPanel value={i + 1} sx={{ padding: "0px !important", my: 3 }} defaultValue={documentTab}>
                        {val?.content}
                    </TabPanel>
                ))}

            </TabContext>
        </Box>
    )
}

export default UploadDocuments